/**
 * Types pour les opportunités internes (importées)
 */

export type OpportunityStatus =
  | 'new'
  | 'qualified'
  | 'in_progress'
  | 'submitted'
  | 'won'
  | 'lost'
  | 'abandoned';

export type Decision = 'go' | 'no_go' | 'pending';

export type CompanyType = 'prospect' | 'client';

export interface Buyer {
  id: number;
  name: string;
  companyType?: CompanyType;
}

/**
 * Configuration d'affichage des types de company
 */
export const COMPANY_TYPE_CONFIG: Record<
  CompanyType,
  { label: string; color: string; bgColor: string }
> = {
  prospect: {
    label: 'Prospect',
    color: 'text-amber-700',
    bgColor: 'bg-amber-100',
  },
  client: {
    label: 'Client',
    color: 'text-green-700',
    bgColor: 'bg-green-100',
  },
};

export interface CurrentStage {
  type: string;
  status: string;
}

export interface TasksStats {
  total: number;
  completed: number;
  overdue: number;
}

export interface AwardCriterion {
  name: string;
  weight: number;
  type: string;
}

/**
 * Rôle d'une organisation dans l'avis
 */
export type BuyerOrganizationRole =
  | 'buyer'
  | 'additional_info'
  | 'document_provider'
  | 'tender_evaluator'
  | 'appeal_receiver'
  | 'appeal_info'
  | 'mediator';

/**
 * Adresse d'une organisation
 */
export interface BuyerOrganizationAddress {
  street?: string;
  additionalStreet?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  nuts?: string;
}

/**
 * Contact d'une organisation
 */
export interface BuyerOrganizationContact {
  name?: string;
  email?: string;
  phone?: string;
}

/**
 * Organisation de l'avis d'appel d'offres (acheteur, info, recours, etc.)
 */
export interface BuyerOrganization {
  id: string;
  name: string;
  registrationNumber?: string;
  department?: string;
  address?: BuyerOrganizationAddress;
  contact?: BuyerOrganizationContact;
  buyerProfileUrl?: string;
  roles: BuyerOrganizationRole[];
}

/**
 * Lot d'une opportunité allotie
 */
export interface OpportunityLot {
  id: number;
  lotNumber: number;
  title: string;
  description?: string;
  estimatedAmount?: number;
  amountMax?: number;
  durationMonths?: number;
  cpvCode?: string;
  status: string;
  decision?: string;
  decisionReason?: string;
}

/**
 * Opportunité interne (importée en base)
 */
export interface Opportunity {
  id: number;
  companyId: number;
  reference?: string;
  externalId?: string;
  title: string;
  shortTitle?: string;
  description?: string;
  buyerId?: number | null;
  buyer?: Buyer;
  buyerName?: string;
  publicationDate?: string;
  submissionDeadline?: string;
  questionsDeadline?: string;
  estimatedAmount?: number;
  amountMin?: number;
  amountMax?: number;
  currency: string;
  durationMonths?: number;
  marketType?: string;
  procedureType?: string;
  isMultiLot: boolean;
  lotsCount: number;
  lots?: OpportunityLot[];
  awardCriteria: AwardCriterion[];
  cpvCodes: string[];
  department?: string;
  region?: string;
  importSource?: string;
  sourceUrl?: string;
  noticeNature?: string;
  noticeNatureLabel?: string;
  originalNoticeId?: string;
  buyerOrganizations?: BuyerOrganization[];
  status: OpportunityStatus;
  decision?: Decision;
  decisionDate?: string;
  decisionReason?: string;
  tags: string[];
  notes?: string;
  currentStage?: CurrentStage;
  myRoles?: string[];
  tasksStats?: TasksStats;
  daysUntilDeadline?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  createdById?: number;
}

/**
 * Statistiques par statut
 */
export interface StatusStats {
  new: number;
  qualified: number;
  in_progress: number;
  submitted: number;
  won: number;
  lost: number;
  abandoned: number;
}

/**
 * Métadonnées de pagination avec stats
 */
export interface PaginationMeta {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  stats?: {
    byStatus: StatusStats;
  };
}

/**
 * Réponse paginée des opportunités
 */
export interface PaginatedOpportunities {
  data: Opportunity[];
  meta: PaginationMeta;
}

/**
 * Filtres pour la liste des opportunités
 */
export interface OpportunityListFilters {
  search?: string;
  status?: OpportunityStatus[];
  decision?: Decision;
  buyerId?: number;
  deadlineFrom?: string;
  deadlineTo?: string;
  minAmount?: number;
  maxAmount?: number;
  marketType?: string;
  importSource?: string;
  assignedToMe?: boolean;
}

/**
 * Statistiques globales des opportunités
 */
export interface OpportunityStats {
  total: number;
  byStatus: StatusStats;
  dueSoon: number;
  overdue: number;
  totalValue: number;
}

/**
 * Configuration d'affichage des statuts
 */
export const STATUS_CONFIG: Record<
  OpportunityStatus,
  { label: string; color: string; bgColor: string }
> = {
  new: {
    label: 'Nouveau',
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
  },
  qualified: {
    label: 'Qualifié',
    color: 'text-purple-700',
    bgColor: 'bg-purple-100',
  },
  in_progress: {
    label: 'En cours',
    color: 'text-amber-700',
    bgColor: 'bg-amber-100',
  },
  submitted: {
    label: 'Soumis',
    color: 'text-cyan-700',
    bgColor: 'bg-cyan-100',
  },
  won: {
    label: 'Gagné',
    color: 'text-green-700',
    bgColor: 'bg-green-100',
  },
  lost: {
    label: 'Perdu',
    color: 'text-red-700',
    bgColor: 'bg-red-100',
  },
  abandoned: {
    label: 'Abandonné',
    color: 'text-gray-700',
    bgColor: 'bg-gray-100',
  },
};

/**
 * Configuration d'affichage des décisions
 */
export const DECISION_CONFIG: Record<
  Decision,
  { label: string; color: string; bgColor: string }
> = {
  go: {
    label: 'GO',
    color: 'text-green-700',
    bgColor: 'bg-green-100',
  },
  no_go: {
    label: 'NO-GO',
    color: 'text-red-700',
    bgColor: 'bg-red-100',
  },
  pending: {
    label: 'En attente',
    color: 'text-gray-700',
    bgColor: 'bg-gray-100',
  },
};

/**
 * Colonne du pipeline Kanban
 */
export interface PipelineColumn {
  id: OpportunityStatus;
  title: string;
  count: number;
  opportunities: Opportunity[];
}

/**
 * Réponse du pipeline
 */
export interface PipelineResponse {
  columns: PipelineColumn[];
}

/**
 * Statuts affichés dans le Kanban (hors états terminaux)
 */
export const PIPELINE_STATUSES: OpportunityStatus[] = [
  'new',
  'qualified',
  'in_progress',
  'submitted',
];
